// Notifications (charte D-038) : cloche de la barre supérieure (compteur et dernières notifications),
// centre de notifications (historique paginé, filtre des non lues, lecture) et préférences par type et
// par canal. Un canal externe non configuré sur le serveur est présenté comme tel et ne peut pas être
// choisi : aucun envoi n'est simulé.

import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone, type NomIcone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { IconeH2, type NomIconeH2 } from '../messagerie/icones';
import { dateCourte } from '../messagerie/api';
import { intervalleSecours, LIBELLES_ETAT_FLUX, useEtatFlux } from './tempsReel';
import './notifications.css';

export interface NotificationVue {
  id: string;
  type: string;
  libelleType: string;
  titre: string;
  contenu: string;
  niveau: string | null;
  objetType: string | null;
  objetId: string | null;
  lien: string | null;
  occurrences: number;
  luLe: string | null;
  creeLe: string;
  modifieLe: string | null;
}

interface PreferencesNotifications {
  types: { code: string; libelle: string }[];
  canaux: { code: string; libelle: string; configure: boolean }[];
  preferences: { type: string; canal: string; actif: boolean; obligatoire: boolean; disponible: boolean }[];
}

export const CLE_NOTIFICATIONS = ['notifications'] as const;

const PICTOGRAMMES: Record<string, { commun?: NomIcone; h2?: NomIconeH2 }> = {
  message: { commun: 'message' },
  annonce: { h2: 'annonce' },
  decision: { h2: 'cadenas' },
  alerte: { commun: 'incident' },
  incident: { commun: 'incident' },
  livraison: { commun: 'camion' },
  cargaison: { commun: 'camion' },
  inspection: { commun: 'controle' },
  rapprochement: { commun: 'document' },
  systeme: { commun: 'aide' },
};

function Pictogramme({ type }: { type: string }) {
  const p = PICTOGRAMMES[type] ?? { commun: 'liste' };
  return (
    <span className="notif-picto">
      {p.h2 ? <IconeH2 nom={p.h2} taille={20} /> : <Icone nom={p.commun ?? 'liste'} taille={20} />}
    </span>
  );
}

function useCompte() {
  const etat = useEtatFlux();
  return useQuery({
    queryKey: [...CLE_NOTIFICATIONS, 'compte'],
    queryFn: async () => (await api<{ nonLues: number }>('/api/v1/notifications/compte')).donnees.nonLues,
    refetchInterval: intervalleSecours(etat),
  });
}

function useLecture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | null) =>
      id
        ? api(`/api/v1/notifications/${id}/lecture`, { corps: {} })
        : api('/api/v1/notifications/lecture-globale', { corps: {} }),
    onSuccess: () => client.invalidateQueries({ queryKey: CLE_NOTIFICATIONS }),
  });
}

function ElementNotification({
  n,
  surOuverture,
  compact = false,
}: {
  n: NotificationVue;
  surOuverture: (n: NotificationVue) => void;
  compact?: boolean;
}) {
  const date = n.modifieLe ?? n.creeLe;
  return (
    <li
      className={`notif-element${n.luLe ? '' : ' non-lue'}${n.niveau === 'elevee' || n.niveau === 'critique' ? ' importante' : ''}`}
    >
      <button type="button" className="notif-ouvrir" onClick={() => surOuverture(n)}>
        <Pictogramme type={n.type} />
        <span className="notif-texte">
          <span className="notif-type">
            {n.libelleType}
            {!n.luLe && <span className="notif-marque">Non lue</span>}
          </span>
          <b className="notif-titre">
            {n.titre}
            {n.occurrences > 1 && <span className="attenue"> ({n.occurrences} nouveaux)</span>}
          </b>
          <span className={`notif-contenu${compact ? ' compact' : ''}`}>{n.contenu}</span>
        </span>
        <time className="notif-date" dateTime={date} title={dateHeure(date)}>
          {dateCourte(date)}
        </time>
      </button>
    </li>
  );
}

/** Cloche de la barre supérieure, avec le panneau des dernières notifications. */
export function ClocheNotifications() {
  const [ouvert, setOuvert] = useState(false);
  const compte = useCompte();
  const lecture = useLecture();
  const naviguer = useNavigate();
  const lieu = useLocation();
  const conteneur = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const idPanneau = useId();
  const recentes = useQuery({
    queryKey: [...CLE_NOTIFICATIONS, 'recentes'],
    queryFn: async () => (await api<NotificationVue[]>('/api/v1/notifications?limite=8')).donnees,
    enabled: ouvert,
  });
  useEffect(() => {
    if (!ouvert) return;
    const surClic = (e: MouseEvent) => {
      if (conteneur.current && !conteneur.current.contains(e.target as Node)) setOuvert(false);
    };
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOuvert(false);
        bouton.current?.focus();
      }
    };
    document.addEventListener('mousedown', surClic);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('mousedown', surClic);
      document.removeEventListener('keydown', surTouche);
    };
  }, [ouvert]);
  // Le panneau se referme quand la page change.
  useEffect(() => setOuvert(false), [lieu.pathname]);

  const nonLues = compte.data ?? 0;
  const ouvrir = (n: NotificationVue) => {
    if (!n.luLe) lecture.mutate(n.id);
    setOuvert(false);
    naviguer(n.lien ?? '/notifications');
  };
  const libelle =
    nonLues > 0
      ? `Notifications, ${nonLues} non lue${nonLues > 1 ? 's' : ''}`
      : 'Notifications, aucune non lue';
  return (
    <div className="notif-cloche" ref={conteneur}>
      <button
        ref={bouton}
        type="button"
        className="bouton notif-bouton"
        aria-label={libelle}
        aria-expanded={ouvert}
        aria-controls={idPanneau}
        onClick={() => setOuvert((v) => !v)}
      >
        <IconeH2 nom="cloche" />
        <span className="libelle-bouton">Notifications</span>
        {nonLues > 0 && (
          <span className="notif-compteur" aria-hidden="true">
            {nonLues > 99 ? '99+' : nonLues}
          </span>
        )}
      </button>
      {ouvert && (
        <div id={idPanneau} className="notif-panneau" role="dialog" aria-label="Dernières notifications">
          <div className="notif-entete">
            <h2>Notifications</h2>
            <button
              type="button"
              className="bouton discret"
              disabled={nonLues === 0 || lecture.isPending}
              onClick={() => lecture.mutate(null)}
            >
              Tout marquer comme lu
            </button>
          </div>
          {recentes.isLoading && <Squelette lignes={3} libelle="Chargement des notifications" />}
          {recentes.error && <MessageErreur erreur={recentes.error} titre="Notifications indisponibles" />}
          {recentes.data && recentes.data.length === 0 && (
            <p className="attenue notif-vide">Aucune notification pour l’instant.</p>
          )}
          {recentes.data && recentes.data.length > 0 && (
            <ul className="notif-liste">
              {recentes.data.map((n) => (
                <ElementNotification key={n.id} n={n} surOuverture={ouvrir} compact />
              ))}
            </ul>
          )}
          <Link to="/notifications" className="bouton notif-centre" onClick={() => setOuvert(false)}>
            Centre de notifications
          </Link>
        </div>
      )}
    </div>
  );
}

type Onglet = 'notifications' | 'preferences';

/** Centre de notifications : historique complet, non lues, lecture et préférences. */
export function PageNotifications() {
  const [onglet, setOnglet] = useState<Onglet>('notifications');
  const [nonLuesSeules, setNonLuesSeules] = useState(false);
  const naviguer = useNavigate();
  const lecture = useLecture();
  const etatFlux = useEtatFlux();
  const compte = useCompte();
  const liste = useInfiniteQuery({
    queryKey: [...CLE_NOTIFICATIONS, 'liste', nonLuesSeules],
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams({ limite: '30' });
      if (nonLuesSeules) p.set('nonLues', 'true');
      if (pageParam) p.set('avant', pageParam);
      return api<NotificationVue[]>(`/api/v1/notifications?${p.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (derniere) => (derniere.meta.suite as string | null) ?? undefined,
    refetchInterval: intervalleSecours(etatFlux),
  });
  const elements = liste.data?.pages.flatMap((p) => p.donnees) ?? [];
  const nonLues = compte.data ?? 0;
  const ouvrir = (n: NotificationVue) => {
    if (!n.luLe) lecture.mutate(n.id);
    if (n.lien) naviguer(n.lien);
  };

  return (
    <div className="page-notifications">
      <div className="page-tete">
        <div>
          <span className="contexte">{LIBELLES_ETAT_FLUX[etatFlux]}</span>
          <h1>Notifications</h1>
          <p>
            {nonLues > 0
              ? `${nonLues} notification${nonLues > 1 ? 's' : ''} non lue${nonLues > 1 ? 's' : ''}.`
              : 'Toutes vos notifications sont lues.'}
          </p>
        </div>
        {onglet === 'notifications' && (
          <button
            type="button"
            className="bouton"
            disabled={lecture.isPending || nonLues === 0}
            onClick={() => lecture.mutate(null)}
          >
            Tout marquer comme lu
          </button>
        )}
      </div>
      <div className="onglets" role="tablist" aria-label="Centre de notifications">
        <button
          type="button"
          role="tab"
          id="onglet-notifications"
          aria-controls="panneau-notifications"
          aria-selected={onglet === 'notifications'}
          onClick={() => setOnglet('notifications')}
        >
          Notifications
        </button>
        <button
          type="button"
          role="tab"
          id="onglet-preferences"
          aria-controls="panneau-preferences"
          aria-selected={onglet === 'preferences'}
          onClick={() => setOnglet('preferences')}
        >
          Préférences
        </button>
      </div>
      {onglet === 'notifications' ? (
        <section id="panneau-notifications" role="tabpanel" aria-labelledby="onglet-notifications">
          <div className="puces espace-bas" role="group" aria-label="Filtre">
            <button
              type="button"
              className="puce"
              aria-pressed={!nonLuesSeules}
              onClick={() => setNonLuesSeules(false)}
            >
              Toutes
            </button>
            <button
              type="button"
              className="puce"
              aria-pressed={nonLuesSeules}
              onClick={() => setNonLuesSeules(true)}
            >
              Non lues <span className="compte">{nonLues}</span>
            </button>
          </div>
          {liste.isLoading && <Squelette lignes={5} libelle="Chargement des notifications" />}
          {liste.error && <MessageErreur erreur={liste.error} titre="Notifications indisponibles" />}
          {liste.data && elements.length === 0 && (
            <div className="etat-vide">
              <span className="pictogramme">
                <IconeH2 nom="cloche" taille={28} />
              </span>
              <h2>{nonLuesSeules ? 'Aucune notification non lue' : 'Aucune notification'}</h2>
              <p>
                Les messages, annonces, décisions et événements qui vous concernent apparaissent ici, en temps
                réel.
              </p>
            </div>
          )}
          {elements.length > 0 && (
            <ul className="notif-liste notif-liste-page">
              {elements.map((n) => (
                <ElementNotification key={n.id} n={n} surOuverture={ouvrir} />
              ))}
            </ul>
          )}
          {liste.hasNextPage && (
            <div className="actions notif-suite">
              <button
                type="button"
                className="bouton"
                disabled={liste.isFetchingNextPage}
                onClick={() => void liste.fetchNextPage()}
              >
                {liste.isFetchingNextPage ? 'Chargement' : 'Notifications plus anciennes'}
              </button>
            </div>
          )}
          {lecture.error && <MessageErreur erreur={lecture.error} titre="Lecture non enregistrée" />}
        </section>
      ) : (
        <section id="panneau-preferences" role="tabpanel" aria-labelledby="onglet-preferences">
          <PreferencesParTypeEtCanal />
        </section>
      )}
    </div>
  );
}

export function PreferencesParTypeEtCanal() {
  const client = useQueryClient();
  const prefs = useQuery({
    queryKey: [...CLE_NOTIFICATIONS, 'preferences'],
    queryFn: async () => (await api<PreferencesNotifications>('/api/v1/notifications-preferences')).donnees,
  });
  const enregistrement = useMutation({
    mutationFn: async (p: { type: string; canal: string; actif: boolean }) =>
      api('/api/v1/notifications-preferences', { methode: 'PUT', corps: { preferences: [p] } }),
    onSuccess: () => client.invalidateQueries({ queryKey: [...CLE_NOTIFICATIONS, 'preferences'] }),
  });
  if (prefs.isLoading) return <Squelette lignes={6} libelle="Chargement des préférences" />;
  if (prefs.error) return <MessageErreur erreur={prefs.error} titre="Préférences indisponibles" />;
  const d = prefs.data!;
  const ligne = (type: string, canal: string) =>
    d.preferences.find((p) => p.type === type && p.canal === canal);
  const nonConfigures = d.canaux.filter((c) => !c.configure);
  return (
    <div className="carte-bloc">
      <div className="carte-bloc-tete">
        <h2>Préférences par type et par canal</h2>
      </div>
      {nonConfigures.length > 0 && (
        <div className="note">
          <Icone nom="aide" />
          <span>
            {nonConfigures.map((c) => c.libelle).join(' et ')} : canal non configuré sur ce serveur, aucun
            envoi n’est effectué. Les notifications dans la plateforme restent actives.
          </span>
        </div>
      )}
      <p className="attenue">
        L’envoi par courriel ou par SMS suppose aussi l’interrupteur général du canal et le niveau minimal
        choisis dans vos préférences. Les décisions officielles et les informations du système sont toujours
        notifiées dans la plateforme.
      </p>
      {enregistrement.error && <MessageErreur erreur={enregistrement.error} titre="Enregistrement refusé" />}
      <div className="tableau-conteneur">
        <table className="tableau">
          <caption className="visuellement-cache">Préférences de notification par type et par canal</caption>
          <thead>
            <tr>
              <th scope="col">Type</th>
              {d.canaux.map((c) => (
                <th key={c.code} scope="col">
                  {c.libelle}
                  {!c.configure && <small className="secondaire">non configuré</small>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.types.map((t) => (
              <tr key={t.code}>
                <th scope="row">{t.libelle}</th>
                {d.canaux.map((c) => {
                  const p = ligne(t.code, c.code);
                  if (!p) return <td key={c.code} />;
                  const verrou = p.obligatoire || !p.disponible;
                  return (
                    <td key={c.code}>
                      <label className="case case-compacte">
                        <input
                          type="checkbox"
                          checked={p.actif && p.disponible}
                          disabled={verrou || enregistrement.isPending}
                          onChange={(e) =>
                            enregistrement.mutate({ type: t.code, canal: c.code, actif: e.target.checked })
                          }
                        />
                        <span>
                          {p.obligatoire
                            ? 'Toujours'
                            : !p.disponible
                              ? 'Indisponible'
                              : p.actif
                                ? 'Activé'
                                : 'Désactivé'}
                        </span>
                        <span className="visuellement-cache">
                          {t.libelle}, {c.libelle}
                        </span>
                      </label>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {enregistrement.isSuccess && (
        <Message type="succes">
          Préférence enregistrée sur le serveur, appliquée sur tous vos appareils.
        </Message>
      )}
    </div>
  );
}
